import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { type Color, colors } from "../../shared/colors";

export type MeshWeight = 200 | 400 | 600 | 800 | 1000;

type MeshVisual = {
  color: string;
  tickerColors?: string[];
  muted: boolean;
};

export type MeshCircle = MeshVisual & {
  cx: number;
  cy: number;
  r: number;
  key: string;
  row: number;
  col: number;
};

export type MeshSquare = MeshVisual & {
  x: number;
  y: number;
  size: number;
  key: string;
};

export type MeshModel = {
  circles: MeshCircle[];
  squares: MeshSquare[];
};

type BuildContext = {
  rows: number;
  columns: number;
  random: () => number;
};

const tickerTimings = [40, 50, 70, 90, 120];
const tickerFrameCount = tickerTimings.length;

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index++) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return hash >>> 0;
}

function seededRandom(seed: number) {
  return () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
}

export function shuffleMeshColors(): Color[] {
  const shuffled = [...colors].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.floor(Math.random() * 4) + 1);
}

export function buildMeshPalette(
  random: () => number,
  count: number,
  weights: readonly MeshWeight[],
  restrictedColors?: readonly Color[]
) {
  const colorList = restrictedColors ?? colors;
  const palette = colorList
    .flatMap((color) => weights.map((weight) => `var(--${color}-${weight})`))
    .sort(() => random() - 0.5)
    .slice(0, count);

  for (const weight of weights) {
    if (!palette.some((color) => color.endsWith(`-${weight})`))) {
      const color = colorList[Math.floor(random() * colorList.length)]!;
      palette[Math.floor(random() * palette.length)] =
        `var(--${color}-${weight})`;
    }
  }
  return palette;
}

export function randomMeshColor(random: () => number, palette: string[]) {
  return palette[Math.floor(random() * palette.length)]!;
}

export function buildTickerColors(getColor: () => string) {
  return Array.from({ length: tickerFrameCount }, getColor);
}

export function buildMeshCircles(
  rows: number,
  columns: number,
  getVisual: (row: number, col: number, grid: MeshCircle[][]) => MeshVisual
) {
  const grid: MeshCircle[][] = [];
  const circles: MeshCircle[] = [];
  for (let row = 0; row < rows; row++) {
    const gridRow: MeshCircle[] = [];
    for (let col = 0; col < columns; col++) {
      const circle = {
        ...getVisual(row, col, grid),
        cx: col + 0.5,
        cy: rows - row - 0.5,
        r: 0.5,
        key: `${row}-${col}`,
        row,
        col,
      };
      gridRow.push(circle);
      circles.push(circle);
    }
    grid.push(gridRow);
  }
  return { circles, grid };
}

type Cell = [number, number];

const squareCorners = ["tl", "tr", "bl", "br"] as const;

export function buildMeshSquares(
  rows: number,
  columns: number,
  getVisual: (
    gridRow: number,
    gridCol: number,
    adjacentCells: Cell[]
  ) => MeshVisual
) {
  const squares: MeshSquare[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      for (const corner of squareCorners) {
        const top = corner[0] === "t";
        const left = corner[1] === "l";
        const verticalNeighbor = top ? 1 : -1;
        const horizontalNeighbor = left ? -1 : 1;
        const gridRow = row + (top ? 1 : 0);
        const gridCol = col + (left ? 0 : 1);
        const adjacentCells: Cell[] = [
          [row, col],
          [row, col + horizontalNeighbor],
          [row + verticalNeighbor, col],
          [row + verticalNeighbor, col + horizontalNeighbor],
        ];
        squares.push({
          ...getVisual(gridRow, gridCol, adjacentCells),
          x: col + (left ? -0.01 : 0.49),
          y: rows - row - 1 + (top ? -0.01 : 0.49),
          size: 0.52,
          key: `${row}-${col}-${corner}`,
        });
      }
    }
  }
  return squares;
}

export function CircleMeshSvg({
  seed,
  targetCircleSize,
  mutingOpacity,
  animate = true,
  animateFade = false,
  grayscale = false,
  build,
}: {
  seed?: string;
  targetCircleSize: number;
  mutingOpacity: number;
  animate?: boolean;
  animateFade?: boolean;
  grayscale?: boolean;
  build: (context: BuildContext) => MeshModel;
}) {
  const randomSeed = useRef(seed ?? Math.random().toString());
  const containerRef = useRef<SVGSVGElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const desaturateId = useId();
  const [tickerFrame, setTickerFrame] = useState(animate ? 0 : -1);

  useEffect(() => {
    if (!animate || tickerFrame >= tickerFrameCount) return;
    const timeout = setTimeout(
      () => setTickerFrame((frame) => frame + 1),
      tickerTimings[tickerFrame] ?? 100
    );
    return () => clearTimeout(timeout);
  }, [animate, tickerFrame]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setContainerSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  if (!containerSize || containerSize.width === 0) {
    return (
      <svg
        ref={containerRef}
        width="100%"
        height="100%"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      />
    );
  }

  const columns = Math.max(
    1,
    Math.round(containerSize.width / targetCircleSize)
  );
  const rows = Math.ceil(
    columns * (containerSize.height / containerSize.width)
  );
  const { circles, squares } = build({
    rows,
    columns,
    random: seededRandom(hashString(randomSeed.current)),
  });
  const displayColor = (visual: MeshVisual) =>
    !animate || tickerFrame < 0 || tickerFrame >= tickerFrameCount
      ? visual.color
      : (visual.tickerColors?.[tickerFrame] ?? visual.color);
  const renderSquare = (square: MeshSquare) => (
    <rect
      key={square.key}
      x={square.x}
      y={square.y}
      width={square.size}
      height={square.size}
      fill={displayColor(square)}
    />
  );
  const renderCircle = (circle: MeshCircle) => (
    <circle
      key={circle.key}
      cx={circle.cx}
      cy={circle.cy}
      r={circle.r}
      fill={displayColor(circle)}
    />
  );

  return (
    <svg
      ref={containerRef}
      width="100%"
      height="100%"
      viewBox={`0 0 ${columns} ${rows}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMax slice"
      style={{
        ...(animate && animateFade
          ? {
              animation: "circleMeshFadeIn 0.5s ease-out",
              transformOrigin: "center bottom",
            }
          : null),
        ...(grayscale ? { filter: "grayscale(1) opacity(0.7)" } : null),
      }}
    >
      <defs>
        {animate && animateFade && (
          <style>{`@keyframes circleMeshFadeIn { from { opacity: 0; transform: scale(1.04); } to { opacity: 1; transform: scale(1); } }`}</style>
        )}
        <filter id={desaturateId} colorInterpolationFilters="sRGB">
          <feColorMatrix
            in="SourceGraphic"
            type="saturate"
            values="0"
            result="gray"
          />
          <feFlood
            floodColor="var(--surface-primary)"
            floodOpacity="1"
            result="tint"
          />
          <feComposite
            in="gray"
            in2="tint"
            operator="arithmetic"
            k1="0"
            k2={mutingOpacity}
            k3={1 - mutingOpacity}
            k4="0"
            result="mixed"
          />
          <feComposite in="mixed" in2="SourceAlpha" operator="in" />
        </filter>
      </defs>
      <g filter={`url(#${desaturateId})`}>
        {squares.filter((square) => square.muted).map(renderSquare)}
      </g>
      {squares.filter((square) => !square.muted).map(renderSquare)}
      <g filter={`url(#${desaturateId})`}>
        {circles.filter((circle) => circle.muted).map(renderCircle)}
      </g>
      {circles.filter((circle) => !circle.muted).map(renderCircle)}
    </svg>
  );
}
